/**
 * **音乐时钟** —— 「音乐走到下一拍 / 下一小节了」这件事在界面这一侧的落点。
 *
 * ## 为什么界面需要一个"音乐的时间"
 *
 * 项目里有两种时间轴，不能混：
 *
 * | 轴 | 谁在推 | 用途 |
 * | --- | --- | --- |
 * | `ticker.deltaMS` | Pixi | 动画：缓动、弹簧、过渡 |
 * | **拍 / 小节** | `BgmDirector` 的 `beat` / `bar` 事件 | **跟着音乐跳的东西** |
 *
 * 后者不能用前者凑：`deltaMS` 累加出来的"大约一拍"和真实的鼓点会越走越偏
 * （BPM 变了、标签页被挂起、解码晚了几百毫秒），几分钟后就是肉眼可见的错拍。
 * 所以拍子必须**由音乐自己报**（`BgmDirector` 用 Tone 的 `Draw.schedule`
 * 在音频时间轴上排回调），界面只管接。
 *
 * ## 两条流，三处落点
 *
 * ```
 *   BgmDirector ──bar──▶ MusicClock ──┬──▶ 背景标量场：从正中推一圈脉冲
 *                                     ├──▶ 濒死层：跟着敲一下重音
 *                                     └──▶ 当前那一屏：手牌 / 牌背脉动
 *                ──beat─▶            └──▶ 同上，但**只在终局**
 * ```
 *
 * ⚠️ **牌面的脉动在终局里改跟拍**（评审：「isFinal 为真时，牌面音乐动效不再
 * 监听 Bar 事件，改为监听 beat 事件」）。终局那一首是 160 BPM、一小节 1.5 秒；
 * 每一拍跳一下就是 0.375 秒一下 —— 比小节快四倍，正好是"最后关头"该有的密度。
 * 哪一条流驱动牌面由 `drivesCardPulse()` **一处**决定，界面不用自己判。
 *
 * 背景那一圈中心脉冲**不跟着换**：每拍从正中推一圈会糊成一团，
 * 而且它本来描的就是"小节"这种更大的呼吸。
 *
 * ## 强度：重音落在乐句头
 *
 * `barInPhrase === 0` 是乐句的第一小节（`score.zenith` 里两小节一句），
 * 把它当成重音：背景那圈脉冲推得更狠。于是画面上的律动是 **强-弱-强-弱**，
 * 而不是每小节一模一样 —— 后者看久了像心跳过速。
 *
 * ## 没有备用节拍器
 *
 * 音乐没响（被自动播放策略拦下、静音、解码失败）就没有事件，画面也就不会跟着
 * 跳。这是有意的：这一层的语义是"和音乐同步"，不是"随便按一个速度动"。
 * 给它配一个自己的节拍器，只会在音乐真正开始之后变成两套对不上的拍子。
 */

import { t } from '../i18n';

/**
 * 一小节。
 *
 * ⚠️ 字段刻意只有这两个：`BgmDirector` 的 `BarEvent` 还带一个音频时间戳，
 * 而界面**不该**拿它去和 `performance.now()` 比 —— 两条轴不是一个原点。
 * 需要"现在离下小节还有多远"的话问指挥器要 `getTiming()`，不要在界面里算差。
 */
export interface BarTick {
  /** 这一 cue 的时钟原点算起第几小节。换 cue 会重数 */
  readonly bar: number;
  /** 乐句里的第几小节。**0 = 乐句头**（重音在这里） */
  readonly barInPhrase: number;
}

/** 一拍。`beatInBar === 0` 就是小节头，那一拍同时也是 `bar` 事件 */
export interface BeatTick {
  readonly bar: number;
  /** 小节里的第几拍。0 = 小节头 */
  readonly beatInBar: number;
  /** 每小节几拍 */
  readonly beatsPerBar: number;
}

/**
 * **音乐上的"一下"** —— 牌面脉动跟着它跳。
 *
 * 平常一下 = 一小节；终局里一下 = 一拍。见 `drivesCardPulse()`。
 */
export interface PulseTick {
  /** 这一下是**小节**（平常）还是**拍**（终局） */
  readonly kind: 'bar' | 'beat';
  readonly bar: number;
  /** 这一下在小节里的位置。`kind === 'bar'` 时是 0 */
  readonly beatInBar: number;
}

export type BarHandler = (tick: BarTick) => void;
export type BeatHandler = (tick: BeatTick) => void;
export type PulseHandler = (tick: PulseTick) => void;

export interface MusicClock {
  /** 每小节一次。背景的中心脉冲、濒死重音走它 */
  onBar(handler: BarHandler): () => void;
  /** 每一拍一次。**终局**里牌面脉动走它 */
  onBeat(handler: BeatHandler): () => void;
  /**
   * 广播一小节。**只有 BGM 适配器（`main.ts`）调它。**
   *
   * ⚠️ 订阅者抛异常不会打断其他订阅者 —— 它是在音频的调度回调里被调的，
   * 让一个画不出来的动画把音乐搞挂是不划算的。
   */
  fireBar(tick: BarTick): void;
  /** 广播一拍。同上 */
  fireBeat(tick: BeatTick): void;
  /** `fireBar` 过几次。调试与冒烟测试用 */
  readonly bars: number;
  /** `fireBeat` 过几次。调试与冒烟测试用 */
  readonly beats: number;
  /** 现在挂着几个订阅者（bar + beat）。调试用 */
  readonly listeners: number;
}

/**
 * 乐句头 / 其余小节的强度。
 *
 * ⚠️ 这是**乘法系数**而不是"两个相对量" —— 背景拿它去乘 `BAR_PULSE.amp`。
 * 所以重音那个必须是 1（"这个脉冲本该有的强度"），弱的那个才是它的折扣。
 */
export const BAR_ACCENT = { downbeat: 1, off: 0.62 } as const;

/** 这一小节该有多重。见 `BAR_ACCENT` */
export function barAccent(tick: BarTick): number {
  return tick.barInPhrase === 0 ? BAR_ACCENT.downbeat : BAR_ACCENT.off;
}

/**
 * **这一下该不该让牌面脉动？**
 *
 * 平常只认小节（一拍跳一下会太碎），**终局里只认拍**（评审要求）。
 *
 * ⚠️ 收成一个纯函数而不是散在宿主里的两个 `if`：这条规则是**会变**的
 * （"终局跟拍"本身就是后加的），而它错了的表现只是"跳得不太对"，
 * 没有任何东西会报错。所以它必须在 node 里有一条断言。
 */
export function drivesCardPulse(kind: PulseTick['kind'], isFinal: boolean): boolean {
  return isFinal ? kind === 'beat' : kind === 'bar';
}

export function createMusicClock(): MusicClock {
  const barHandlers = new Set<BarHandler>();
  const beatHandlers = new Set<BeatHandler>();
  let bars = 0;
  let beats = 0;

  const safely = <T>(handlers: Iterable<(tick: T) => void>, tick: T): void => {
    for (const handler of handlers) {
      try {
        handler(tick);
      } catch (error) {
        // 见 `fireBar` 的说明：一个订阅者画不出来，不该让这一下的其他人也收不到
        console.error(t('beat.subscriberThrew'), error);
      }
    }
  };

  return {
    onBar(handler) {
      barHandlers.add(handler);
      return () => {
        barHandlers.delete(handler);
      };
    },
    onBeat(handler) {
      beatHandlers.add(handler);
      return () => {
        beatHandlers.delete(handler);
      };
    },
    fireBar(tick) {
      bars++;
      safely(barHandlers, tick);
    },
    fireBeat(tick) {
      beats++;
      safely(beatHandlers, tick);
    },
    get bars() {
      return bars;
    },
    get beats() {
      return beats;
    },
    get listeners() {
      return barHandlers.size + beatHandlers.size;
    },
  };
}

/**
 * **一个永远不会响的时钟。**
 *
 * 给"没有音频"的场合（测试、调试页、`ScreenContext` 没接线时）用，
 * 免得每个调用点都写一遍 `?.` 和一个空函数。
 */
export const SILENT_MUSIC_CLOCK: MusicClock = Object.freeze({
  onBar: () => () => {},
  onBeat: () => () => {},
  fireBar: () => {},
  fireBeat: () => {},
  bars: 0,
  beats: 0,
  listeners: 0,
});
